/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import { useNavigate } from "react-router-dom";

import HotelProfileHeader from "./HotelProfileHeader";
import HotelDashboardCards from "./HotelDashboardCards";
import HotelEditModal from "./HotelEditModal";

import "./HotelProfilePage.css";

const HotelProfilePage = () => {
  /* ───────── auth & navigation ───────── */
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;
  const navigate = useNavigate();

  /* ───────── data state ───────── */
  const [hotel, setHotel] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  /* ───────── modal state ───────── */
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState({});
  const [currentSection, setCurrentSection] = useState("General");
  const [editField, setEditField] = useState("");

  /* ───────── fetch hotel ───────── */
  useEffect(() => {
    if (!hotelId) {
      setError("Hotel-ID missing.");
      return;
    }

    const fetchHotel = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/Hotel/${hotelId}`);
        setHotel(res.data);
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load hotel.");
      } finally {
        setLoading(false);
      }
    };

    fetchHotel();
  }, [hotelId]);

  /* ───────── navigation handler ───────── */
  const handleNavigation = (path) => {
    navigate(path);
  };

  /* ───────── open / close modal ───────── */
  const openModal = (section = "General") => {
    setEditData({ ...hotel }); // shallow copy
    setCurrentSection(section);
    setEditField("");
    setIsModalOpen(true);
  };

  const closeModal = () => setIsModalOpen(false);

  /* ───────── save one field ───────── */
  const handleSaveField = async () => {
    if (!hotelId || !editField) return;

    // build minimal DTO (only scalars & simple arrays)
    const {
      hotelName,
      address,
      contactNumber,
      email,
      website,
      socialMediaHandle,
      mapCoordinates,
      physicalLocation,
      townOrVillage,
      district,
      province,
      country,
      description,
      numberOfRooms,
      type,
      amenities,
      activities,
    } = editData;

    const dto = {
      hotelName,
      address,
      contactNumber,
      email,
      website,
      socialMediaHandle,
      mapCoordinates,
      physicalLocation,
      townOrVillage,
      district,
      province,
      country,
      description,
      numberOfRooms,
      type,
      amenities,
      activities,
    };

    try {
      setSaving(true);
      const res = await axiosInstance.put(`/Hotel/${hotelId}`, dto);
      setHotel(res.data); // refresh UI
      setEditField("");
      closeModal();
    } catch (e) {
      setError(e.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  /* ───────── render ───────── */
  if (loading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="hotel-dashboard">
      {saving && <div className="saving-overlay">Saving…</div>}

      <HotelProfileHeader
        hotelName={hotel?.hotelName}
        onEditClick={() => openModal("General")}
      />

      <HotelDashboardCards onNavigate={handleNavigation} />

      <HotelEditModal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        hotel={hotel}
        editData={editData}
        setEditData={setEditData}
        currentSection={currentSection}
        setCurrentSection={setCurrentSection}
        editField={editField}
        setEditField={setEditField}
        onSave={handleSaveField}
      />
    </div>
  );
};

export default HotelProfilePage;